import {
  PaginatedResponse,
  ProductSearchItem,
  AdminProductSearchItem,
  ManagedStrategySearchItem,
} from "../types/productV3";
import httpService from "./httpService";

export interface ProductSearchParams {
  page?: number;
  pageSize?: number;
  productType?: "BASKET" | "PORTFOLIO";
  providerId?: number;
  productId?: number;
  keyword?: string;
}

export type AdminSortBy = "title" | "createdAtUtc" | "updatedAtUtc";
export type SortDirection = "asc" | "desc";

export interface AdminProductSearchParams {
  page?: number;
  pageSize?: number;
  productType?: "BASKET" | "PORTFOLIO";
  providerId?: number;
  productId?: number;
  keyword?: string;
  visibility?: "PUBLIC" | "PRIVATE" | "SUBSCRIBER";
  sortBy?: AdminSortBy;
  sortDirection?: SortDirection;
}

// Standalone params for searchAdminProductsAllTypes -- deliberately NOT the
// same interface as AdminProductSearchParams, so widening productType here can
// never leak into searchAdminProducts()'s existing (narrower) callers.
export interface AdminAllTypesSearchParams {
  page?: number;
  pageSize?: number;
  productType?: "BASKET" | "PORTFOLIO" | "MANAGED_STRATEGY";
  providerId?: number;
  productId?: number;
  keyword?: string;
  visibility?: "PUBLIC" | "PRIVATE" | "SUBSCRIBER";
  sortBy?: AdminSortBy;
  sortDirection?: SortDirection;
}

export type ProductSearchResponse = PaginatedResponse<ProductSearchItem>;

export type ManagedStrategySearchResponse = PaginatedResponse<ManagedStrategySearchItem>;

export type AdminProductSearchResponse = PaginatedResponse<AdminProductSearchItem>;

function buildQueryString(entries: [string, string | undefined][]): string {
  const qs = new URLSearchParams();
  for (const [key, value] of entries) {
    if (value !== undefined && value !== "") qs.set(key, value);
  }
  return qs.toString();
}

class ProductSearchService {
  async searchProducts(
    params: ProductSearchParams,
    signal?: AbortSignal
  ): Promise<ProductSearchResponse> {
    const queryString = buildQueryString([
      ["page", params.page !== undefined ? String(params.page) : undefined],
      ["pageSize", params.pageSize !== undefined ? String(params.pageSize) : undefined],
      ["productType", params.productType],
      ["providerId", params.providerId !== undefined ? String(params.providerId) : undefined],
      ["productId", params.productId !== undefined ? String(params.productId) : undefined],
      ["keyword", params.keyword],
    ]);
    const url = queryString
      ? `/v3/products/search?${queryString}`
      : "/v3/products/search";

    const response = await httpService.get<
      PaginatedResponse<ProductSearchItem | ManagedStrategySearchItem>
    >(url, { signal });
    // GET /v3/products/search also returns MANAGED_STRATEGY rows (providerName
    // "Plutus", null providerId) when no productType filter is passed, but every
    // caller of this method is typed against ProductSearchItem (BASKET/PORTFOLIO,
    // non-null providerId), so strip them here rather than at each call site --
    // same approach as searchAdminProducts below. Use getManagedStrategyForInvestor()
    // when a MANAGED_STRATEGY row is actually needed.
    const items = response.data.items.filter(
      (item): item is ProductSearchItem => item.productType !== "MANAGED_STRATEGY"
    );
    return {
      ...response.data,
      items,
      // totalItems/totalPages are left as the backend reported them -- callers
      // page through totalPages, and shrinking these could make a caller stop
      // short of a real page. itemsOnPage reflects this page's returned array.
      itemsOnPage: items.length,
    };
  }

  async getManagedStrategyForInvestor(
    strategyId: number,
    signal?: AbortSignal
  ): Promise<ManagedStrategySearchItem> {
    const response = await httpService.get<ManagedStrategySearchResponse>(
      `/v3/products/search?productType=MANAGED_STRATEGY&productId=${strategyId}&page=1&pageSize=1`,
      { signal }
    );
    const [item] = response.data.items;

    if (
      response.data.items.length !== 1 ||
      item.productType !== "MANAGED_STRATEGY" ||
      item.productId !== strategyId
    ) {
      throw new Error(`Managed strategy ${strategyId} was not returned by product search.`);
    }

    return item;
  }

  /**
   * Admin-scoped twin of getManagedStrategyForInvestor(). The admin route
   * applies no visibility floor, so a PRIVATE managed strategy still resolves
   * -- required when an admin views another user's book.
   *
   * Deliberately not routed through searchAdminProducts() (which strips
   * MANAGED_STRATEGY rows by design) or searchAdminProductsAllTypes() (whose
   * AdminProductSearchItem types `snapshot` as the basket/portfolio union and
   * would need an unsafe cast at every call site).
   */
  async getManagedStrategyForAdmin(
    strategyId: number,
    signal?: AbortSignal
  ): Promise<ManagedStrategySearchItem> {
    const response = await httpService.get<ManagedStrategySearchResponse>(
      `/v3/products/search/admin?productType=MANAGED_STRATEGY&productId=${strategyId}&page=1&pageSize=1`,
      { signal }
    );
    const [item] = response.data.items;

    if (
      response.data.items.length !== 1 ||
      item.productType !== "MANAGED_STRATEGY" ||
      item.productId !== strategyId
    ) {
      throw new Error(`Managed strategy ${strategyId} was not returned by admin product search.`);
    }

    return item;
  }

  async getProductForInvestor(
    productType: "BASKET" | "PORTFOLIO",
    productId: number,
    signal?: AbortSignal
  ): Promise<ProductSearchItem> {
    const response = await httpService.get<ProductSearchResponse>(
      `/v3/products/search?productType=${productType}&productId=${productId}&page=1&pageSize=1`,
      { signal }
    );
    const [item] = response.data.items;

    if (
      response.data.items.length !== 1 ||
      item.productType !== productType ||
      item.productId !== productId
    ) {
      throw new Error(`${productType} ${productId} was not returned by product search.`);
    }

    return item;
  }

  async getManagedStrategyByMixIdForInvestor(
    mixId: string,
    signal?: AbortSignal
  ): Promise<ManagedStrategySearchItem> {
    const normalizedMixId = mixId.trim();
    if (!normalizedMixId) {
      throw new Error("A managed strategy mix ID is required.");
    }

    const pageSize = 100;
    const matches: ManagedStrategySearchItem[] = [];
    let page = 1;
    let totalPages = 1;

    do {
      const response = await httpService.get<ManagedStrategySearchResponse>(
        `/v3/products/search?productType=MANAGED_STRATEGY&page=${page}&pageSize=${pageSize}`,
        { signal }
      );
      totalPages = response.data.totalPages;
      matches.push(...response.data.items.filter((item) => (
        item.productType === "MANAGED_STRATEGY"
        && "mixId" in item.settings
        && item.settings.mixId === normalizedMixId
      )));
      page += 1;
    } while (page <= totalPages && matches.length <= 1);

    if (matches.length !== 1) {
      throw new Error(
        matches.length === 0
          ? `No managed strategy was returned for mix ${normalizedMixId}.`
          : `Multiple managed strategies were returned for mix ${normalizedMixId}.`
      );
    }

    return matches[0];
  }

  async searchAdminProducts(
    params: AdminProductSearchParams,
    signal?: AbortSignal
  ): Promise<ProductSearchResponse> {
    const queryString = buildQueryString([
      ["page", params.page !== undefined ? String(params.page) : undefined],
      ["pageSize", params.pageSize !== undefined ? String(params.pageSize) : undefined],
      ["productType", params.productType],
      ["providerId", params.providerId !== undefined ? String(params.providerId) : undefined],
      ["productId", params.productId !== undefined ? String(params.productId) : undefined],
      ["keyword", params.keyword],
      ["visibility", params.visibility],
      ["sortBy", params.sortBy],
      ["sortDirection", params.sortDirection],
    ]);
    const url = queryString
      ? `/v3/products/search/admin?${queryString}`
      : "/v3/products/search/admin";

    const response = await httpService.get<PaginatedResponse<AdminProductSearchItem>>(url, { signal });
    // GET /v3/products/search/admin can return MANAGED_STRATEGY rows (nullable
    // providerId, a snapshot shape isNewSnapshotShape() lets through despite
    // having no `contracts`) even when the caller passes no productType filter
    // -- this method's ~10 existing callers are all typed and written against
    // the narrower ProductSearchItem contract (BASKET/PORTFOLIO, non-null
    // providerId) and don't guard against MANAGED_STRATEGY, so strip it out
    // here rather than at each call site. Use searchAdminProductsAllTypes()
    // instead if a caller actually needs MANAGED_STRATEGY rows.
    const items = response.data.items
      .filter((item) => item.productType !== "MANAGED_STRATEGY")
      // AdminProductSearchItem is a flat (non-discriminated) supertype, so
      // narrowing productType above doesn't narrow providerId's nullability
      // or drop the extra numOfPortfolios field at the type level -- both are
      // true at runtime once MANAGED_STRATEGY (the only type with a null
      // providerId or a populated numOfPortfolios) is filtered out.
      .map(({ numOfPortfolios: _numOfPortfolios, ...rest }) => rest as ProductSearchItem);
    return {
      ...response.data,
      items,
      // totalItems/totalPages are left as the backend reported them (over the
      // full, unfiltered dataset) -- callers page through totalPages to fetch
      // every basket/portfolio, and shrinking these here could make a caller
      // stop short of a real page if MANAGED_STRATEGY rows sit near a page
      // boundary. itemsOnPage is recalculated since it describes this page's
      // actual returned array length.
      itemsOnPage: items.length,
    };
  }

  // Standalone from searchAdminProducts() -- that method already has ~10
  // pre-existing callers across the app typed against its narrower
  // ProductSearchItem-based return shape (see Task 1's plan notes for the
  // full list). This method hits the identical endpoint but is typed to
  // include MANAGED_STRATEGY rows, and is used only by the admin Products
  // page (useAdminProductsList.ts). Do not merge this into searchAdminProducts.
  async searchAdminProductsAllTypes(
    params: AdminAllTypesSearchParams,
    signal?: AbortSignal
  ): Promise<AdminProductSearchResponse> {
    const queryString = buildQueryString([
      ["page", params.page !== undefined ? String(params.page) : undefined],
      ["pageSize", params.pageSize !== undefined ? String(params.pageSize) : undefined],
      ["productType", params.productType],
      ["providerId", params.providerId !== undefined ? String(params.providerId) : undefined],
      ["productId", params.productId !== undefined ? String(params.productId) : undefined],
      ["keyword", params.keyword],
      ["visibility", params.visibility],
      ["sortBy", params.sortBy],
      ["sortDirection", params.sortDirection],
    ]);
    const url = queryString
      ? `/v3/products/search/admin?${queryString}`
      : "/v3/products/search/admin";

    const response = await httpService.get<AdminProductSearchResponse>(url, { signal });
    return response.data;
  }
}

export const productSearchService = new ProductSearchService();
